import React, { useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import ConversationList from './components/ConversationList';
import MessageView from './components/MessageView';
import InfoPanel from './components/InfoPanel';
import WhatsAppSetup from './components/WhatsAppSetup';
import Login from './components/Login';
import StatisticsView from './components/StatisticsView';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function App() {
  // État d'authentification
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState('all');
  const [statusTab, setStatusTab] = useState('active'); // 'active' ou 'archived'
  const [loading, setLoading] = useState(true);
  const [whatsappStatus, setWhatsappStatus] = useState({ connected: false, qrCode: null });
  const [agents, setAgents] = useState([]);
  const [currentAgent, setCurrentAgent] = useState(null);
  const [quickReplies, setQuickReplies] = useState([]);
  const [socket, setSocket] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentView, setCurrentView] = useState('conversations');
  const [ticketViews, setTicketViews] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Vérifier si l'utilisateur est déjà connecté au chargement
  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('auth_token');

    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
      }
    }
    setAuthLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('auth_token');
    setUser(null);
    setConversations([]);
    setSelectedConversation(null);
    setMessages([]);
  };

  // Initialiser Socket.IO
  useEffect(() => {
    const newSocket = io(API_URL);

    newSocket.on('connect', () => {
      console.log('Connecté au serveur');
    });

    newSocket.on('whatsapp:message', (msg) => {
      // Nouveau message WhatsApp reçu
      fetchConversations();
      if (selectedConversation?.id === `wa_${msg.from}`) {
        fetchMessages(selectedConversation);
      }
    });

    newSocket.on('whatsapp:qr', (qr) => {
      setWhatsappStatus(prev => ({ ...prev, qrCode: qr }));
    });

    newSocket.on('whatsapp:ready', () => {
      setWhatsappStatus({ connected: true, qrCode: null });
      fetchConversations();
    });

    newSocket.on('conversation:assigned', ({ conversationId, agentId }) => {
      fetchConversations();
    });

    setSocket(newSocket);

    return () => newSocket.close();
  }, []);

  // Charger les données initiales
  useEffect(() => {
    fetchConversations();
    fetchAgents();
    fetchQuickReplies();
    checkWhatsAppStatus();
  }, []);

  const fetchConversations = async () => {
    try {
      const headers = {};
      // Envoyer les équipes de l'utilisateur pour filtrer côté serveur
      if (user?.teams) {
        headers['X-User-Teams'] = JSON.stringify(user.teams);
      }

      const res = await fetch(`${API_URL}/api/conversations`, { headers });
      const data = await res.json();
      setConversations(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement conversations:', error);
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/agents`);
      const data = await res.json();
      setAgents(data);
      if (data.length > 0 && !currentAgent) {
        setCurrentAgent(data[0]);
      }
    } catch (error) {
      console.error('Erreur chargement agents:', error);
    }
  };

  const fetchQuickReplies = async () => {
    try {
      const res = await fetch(`${API_URL}/api/quick-replies`);
      if (res.ok) {
        const data = await res.json();
        setQuickReplies(data);
      }
    } catch (error) {
      // Quick replies endpoint might not exist yet
      setQuickReplies([
        { id: 1, title: 'Salutation', content: 'Bonjour, merci de nous avoir contactés. Comment puis-je vous aider ?' },
        { id: 2, title: 'Accusé', content: 'Nous avons bien reçu votre demande et nous la traitons dans les plus brefs délais.' },
        { id: 3, title: 'Info', content: 'Pourriez-vous nous fournir plus de détails concernant votre demande ?' },
        { id: 4, title: 'Clôture', content: 'Merci pour votre patience. N\'hésitez pas à nous recontacter si vous avez d\'autres questions.' }
      ]);
    }
  };

  const checkWhatsAppStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/whatsapp/status`);
      const data = await res.json();
      setWhatsappStatus(data);
    } catch (error) {
      console.error('Erreur vérification WhatsApp:', error);
    }
  };

  // Recherche full-text via le backend
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setSearchResults(null);
      setSearching(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const headers = {};
        if (user?.teams) {
          headers['X-User-Teams'] = JSON.stringify(user.teams);
        }
        const res = await fetch(`${API_URL}/api/conversations/search?q=${encodeURIComponent(searchQuery.trim())}`, { headers });
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data);
        }
      } catch (e) {
        console.error('Erreur recherche:', e);
      } finally {
        setSearching(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, user?.teams]);

  const fetchMessages = useCallback(async (conversation) => {
    if (!conversation) return;

    try {
      if (conversation.source === 'odoo') {
        const ticketId = conversation.id.replace('odoo_', '');
        const res = await fetch(`${API_URL}/api/odoo/tickets/${ticketId}`);
        const data = await res.json();
        setMessages(data.messages || []);
      } else if (conversation.source === 'whatsapp') {
        const chatId = conversation.id.replace('wa_', '');
        const instanceId = conversation.instanceId;
        const res = await fetch(`${API_URL}/api/whatsapp/conversations/${chatId}/messages?instanceId=${instanceId}`);
        const data = await res.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Erreur chargement messages:', error);
      setMessages([]);
    }
  }, []);

  const fetchTicketViews = async (conversation) => {
    try {
      const type = conversation.source;
      const id = conversation.waTicketId || conversation.id?.replace('odoo_', '').replace('wa_', '');
      const res = await fetch(`${API_URL}/api/tickets/${type}/${id}/views`);
      if (res.ok) {
        const data = await res.json();
        setTicketViews(data);
      }
    } catch (e) {
      setTicketViews([]);
    }
  };

  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);
    fetchMessages(conversation);
    fetchTicketViews(conversation);

    // Logger la vue du ticket
    if (user) {
      try {
        const type = conversation.source;
        const id = conversation.waTicketId || conversation.id?.replace('odoo_', '').replace('wa_', '');
        await fetch(`${API_URL}/api/tickets/${type}/${id}/view`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ agentId: user.id, agentName: user.name })
        });
      } catch (e) {
        // Silently fail - tracking is not critical
      }
    }
  };

  const handleSendMessage = async (message, isInternal = false) => {
    if (!selectedConversation || !message.trim()) return;

    try {
      if (selectedConversation.source === 'odoo') {
        const ticketId = selectedConversation.id.replace('odoo_', '');
        await fetch(`${API_URL}/api/odoo/tickets/${ticketId}/reply`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message, agentId: user?.id, isInternal })
        });
      } else if (selectedConversation.source === 'whatsapp') {
        const chatId = selectedConversation.id.replace('wa_', '');
        const instanceId = selectedConversation.instanceId;
        await fetch(`${API_URL}/api/whatsapp/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ chatId, message, agentId: user?.id, agentName: user?.name, instanceId })
        });
      }

      // Rafraîchir les messages
      fetchMessages(selectedConversation);
      fetchConversations();
    } catch (error) {
      console.error('Erreur envoi message:', error);
      alert('Erreur lors de l\'envoi du message');
    }
  };

  // Fonction utilitaire: rafraîchir les conversations et mettre à jour la sélection
  const refreshAndUpdateSelection = async (alsoRefreshMessages = false) => {
    try {
      const hdrs = {};
      if (user?.teams) {
        hdrs['X-User-Teams'] = JSON.stringify(user.teams);
      }
      const res = await fetch(`${API_URL}/api/conversations`, { headers: hdrs });
      const data = await res.json();
      setConversations(data);
      if (selectedConversation) {
        const updated = data.find(c => c.id === selectedConversation.id);
        if (updated) {
          setSelectedConversation(updated);
          if (alsoRefreshMessages) {
            fetchMessages(updated);
          }
        }
      }
    } catch (error) {
      console.error('Erreur rafraîchissement:', error);
    }
  };

  const handleAssign = async (agentId) => {
    if (!selectedConversation) return;

    try {
      await fetch(`${API_URL}/api/conversations/${selectedConversation.id}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId })
      });
      await refreshAndUpdateSelection();
    } catch (error) {
      console.error('Erreur assignation:', error);
    }
  };

  const handleUpdatePartner = async (partnerId, options = {}) => {
    if (!selectedConversation || selectedConversation.source !== 'odoo') return;

    try {
      const ticketId = selectedConversation.id.replace('odoo_', '');
      await fetch(`${API_URL}/api/odoo/tickets/${ticketId}/partner`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partnerId,
          agentId: currentAgent?.id,
          isCompany: options.isCompany || false,
          ticketEmail: options.ticketEmail || null,
          ticketName: options.ticketName || null
        })
      });
      await refreshAndUpdateSelection(true);
    } catch (error) {
      console.error('Erreur mise à jour partenaire:', error);
    }
  };

  const handleUpdateStatus = async (stageId) => {
    if (!selectedConversation || selectedConversation.source !== 'odoo') return;

    try {
      const ticketId = selectedConversation.id.replace('odoo_', '');
      await fetch(`${API_URL}/api/odoo/tickets/${ticketId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: stageId, agentId: currentAgent?.id })
      });
      await refreshAndUpdateSelection();
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
    }
  };

  // Liste des statuts considérés comme "archivés" (fin de cycle)
  const archivedStatuses = [
    'résolu', 'resolu', 'resolved', 'done', 'terminé', 'termine',
    'résolu à chaud', 'resolu a chaud', 'resolved hot',
    'annulé', 'annule', 'cancelled', 'canceled',
    'fermé', 'ferme', 'closed',
    'clôturé', 'cloture', 'cloturé',
    'solved', 'complete', 'completed', 'finished', 'terminée'
  ];

  const isArchived = (conversation) => {
    if (!conversation.status) return false;
    const statusLower = conversation.status.toLowerCase().trim();
    return archivedStatuses.some(s => statusLower.includes(s));
  };

  const filteredConversations = (() => {
    // Si on a une recherche active, fusionner les résultats backend avec les conversations locales
    if (searchQuery.trim() && searchQuery.trim().length >= 2 && searchResults) {
      // Fusionner: les résultats serveur (deep search) + filtrage local sur les conversations chargées
      const resultIds = new Set(searchResults.map(r => r.id));
      const query = searchQuery.toLowerCase();

      // Ajouter les conversations locales qui matchent aussi (pour le temps réel)
      const localMatches = conversations.filter(c => {
        if (resultIds.has(c.id)) return false; // Déjà dans les résultats serveur
        const matchesTitle = c.title?.toLowerCase().includes(query);
        const matchesCustomer = c.customer?.toLowerCase().includes(query);
        const matchesLastMessage = c.lastMessage?.toLowerCase().includes(query);
        return matchesTitle || matchesCustomer || matchesLastMessage;
      });

      const merged = [...searchResults, ...localMatches];

      // Appliquer le filtre par source
      return merged.filter(c => {
        if (filter === 'odoo') return c.source === 'odoo';
        if (filter === 'whatsapp') return c.source === 'whatsapp';
        if (filter === 'unread') return c.unread;
        if (filter === 'mine') return c.assignedTo === user?.name || c.assignedToId === user?.id;
        return true;
      });
    }

    // Sans recherche: filtrage classique
    return conversations.filter(c => {
      // Filtre par statut (en cours / archivé)
      const archived = isArchived(c);
      if (statusTab === 'active' && archived) return false;
      if (statusTab === 'archived' && !archived) return false;

      // Pour les archivés sans recherche, limiter aux dernières 24h
      if (statusTab === 'archived') {
        const oneDayAgo = new Date();
        oneDayAgo.setHours(oneDayAgo.getHours() - 24);
        const ticketDate = new Date(c.updatedAt);
        if (ticketDate < oneDayAgo) return false;
      }

      // Filtre par source
      if (filter === 'all') return true;
      if (filter === 'odoo') return c.source === 'odoo';
      if (filter === 'whatsapp') return c.source === 'whatsapp';
      if (filter === 'unread') return c.unread;
      if (filter === 'mine') return c.assignedTo === user?.name || c.assignedToId === user?.id;
      return true;
    });
  })();

  // Compter les tickets par onglet
  const activeCount = conversations.filter(c => !isArchived(c)).length;
  const archivedCount = conversations.filter(c => isArchived(c)).length;

  // Afficher le chargement initial
  if (authLoading) {
    return (
      <div className="login-container">
        <div className="loading">
          <div className="spinner"></div>
          Chargement...
        </div>
      </div>
    );
  }

  // Afficher la page de login si non connecté
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      {currentView === 'conversations' && <div className="sidebar">
        <div className="sidebar-header">
          <div className="header-top">
            <h1>
              <span>Support</span> Hub
            </h1>
            <div className="user-compact">
              <span className="user-avatar" title={user.name}>
                {user.name?.charAt(0).toUpperCase()}
              </span>
              <button className="logout-btn-icon" onClick={handleLogout} title="Déconnexion">
                ⏻
              </button>
            </div>
          </div>
          <div className="user-teams-bar">
            {user.teams?.length > 0
              ? user.teams.map(t => t.name).join(' • ')
              : 'Toutes les équipes'}
          </div>
          <button
            onClick={() => setCurrentView(currentView === 'statistics' ? 'conversations' : 'statistics')}
            style={{
              marginTop: '10px',
              width: '100%',
              padding: '10px 16px',
              background: 'var(--accent)',
              border: 'none',
              borderRadius: '8px',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            <span>📊</span> {currentView === 'statistics' ? 'Conversations' : 'Statistiques'}
          </button>
        </div>

        {/* Onglets En cours / Archivés (masqués pendant la recherche) */}
        {!searchQuery.trim() && (
          <div className="status-tabs">
            <button
              className={`status-tab ${statusTab === 'active' ? 'active' : ''}`}
              onClick={() => setStatusTab('active')}
            >
              📋 En cours
              <span className="tab-count">{activeCount}</span>
            </button>
            <button
              className={`status-tab ${statusTab === 'archived' ? 'active' : ''}`}
              onClick={() => setStatusTab('archived')}
            >
              📦 Archivés
              <span className="tab-count">{archivedCount}</span>
            </button>
          </div>
        )}

        {/* Barre de recherche */}
        <div className="search-bar">
          <input
            type="text"
            placeholder="🔍 Rechercher dans tous les tickets..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searching && (
            <span style={{ position: 'absolute', right: searchQuery ? '40px' : '22px', top: '50%', transform: 'translateY(-50%)', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              ...
            </span>
          )}
          {searchQuery && (
            <button className="clear-search" onClick={() => { setSearchQuery(''); setSearchResults(null); }}>✕</button>
          )}
        </div>
        {searchQuery.trim().length >= 2 && searchResults && (
          <div style={{
            padding: '4px 15px',
            fontSize: '0.7rem',
            color: 'var(--text-muted)',
            background: 'rgba(13, 148, 136, 0.05)',
            borderBottom: '1px solid var(--border)'
          }}>
            {filteredConversations.length} résultat(s) dans tous les tickets
          </div>
        )}

        <div className="filters">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Tous
          </button>
          <button
            className={`filter-btn ${filter === 'mine' ? 'active' : ''}`}
            onClick={() => setFilter('mine')}
          >
            Mes tickets
          </button>
          <button
            className={`filter-btn odoo ${filter === 'odoo' ? 'active' : ''}`}
            onClick={() => setFilter('odoo')}
          >
            Odoo
          </button>
          <button
            className={`filter-btn whatsapp ${filter === 'whatsapp' ? 'active' : ''}`}
            onClick={() => setFilter('whatsapp')}
          >
            WhatsApp
          </button>
        </div>

        <ConversationList
          conversations={filteredConversations}
          selectedId={selectedConversation?.id}
          onSelect={handleSelectConversation}
          loading={loading}
        />
      </div>}

      {currentView === 'statistics' ? (
        <StatisticsView onBack={() => setCurrentView('conversations')} />
      ) : (
        <>
          <div className="main-content">
            {!whatsappStatus.connected && filter === 'whatsapp' ? (
              <WhatsAppSetup qrCode={whatsappStatus.qrCode} />
            ) : selectedConversation ? (
              <MessageView
                conversation={selectedConversation}
                messages={messages}
                onSend={handleSendMessage}
                quickReplies={quickReplies}
                ticketViews={ticketViews}
              />
            ) : (
              <div className="empty-state">
                <div className="empty-state-icon">💬</div>
                <h2>Sélectionnez une conversation</h2>
                <p>Choisissez une conversation dans la liste pour commencer</p>
              </div>
            )}
          </div>

          {selectedConversation && (
            <InfoPanel
              conversation={selectedConversation}
              agents={agents}
              onAssign={handleAssign}
              onUpdatePartner={handleUpdatePartner}
              onUpdateStatus={handleUpdateStatus}
              onAssignOdooUser={() => refreshAndUpdateSelection(true)}
              onAssignOdooTeam={() => refreshAndUpdateSelection(true)}
              onDeleteTicket={(deletedId) => {
                setSelectedConversation(null);
                setMessages([]);
                refreshAndUpdateSelection();
              }}
              onRefresh={() => refreshAndUpdateSelection(true)}
            />
          )}
        </>
      )}
    </div>
  );
}

export default App;
